import { makeAutoObservable, reaction, runInAction, toJS } from "mobx";
import localforage from "localforage";
import type { ApiClient } from "@/clients/ApiClient";

export interface PlaylistItem {
    played: boolean;
    progress: number;
    episode: Episode;
    podcast: Podcast;
}

type PlaylistApi = Pick<ApiClient, "get" | "put">;

interface PlaylistAuth {
    readonly status: "unknown" | "anonymous" | "authenticated";
    readonly isSignedIn: boolean;
}

/** Changes within this time are sent as one save */
const SAVE_DELAY_MS = 400;

/**
 * The listener's saved episodes. Signed in, the list lives on the account (`/me/playlist`) and
 * follows them between devices; every change sends the whole ordered list. Signed out, it is kept
 * on this device as before, so being signed out is never a worse experience. The device copy is
 * left alone on sign-in, for importing into the account.
 */
export class PlaylistStore {
    filter = "";
    episodes: PlaylistItem[] = [];
    isOpen = false;

    private generation = 0;
    /** The ids last known to be on the server, so a list that was just loaded is not sent back */
    private syncedKey = "";
    private saveTimer: ReturnType<typeof setTimeout> | undefined;
    private saving = false;
    private dirty = false;

    constructor(
        private readonly client: PlaylistApi,
        private readonly auth: PlaylistAuth
    ) {
        makeAutoObservable<
            PlaylistStore,
            "client" | "auth" | "generation" | "syncedKey" | "saveTimer" | "saving" | "dirty"
        >(this, {
            client: false,
            auth: false,
            generation: false,
            syncedKey: false,
            saveTimer: false,
            saving: false,
            dirty: false,
        });

        reaction(
            () => this.auth.status,
            (status) => {
                this.generation++;
                clearTimeout(this.saveTimer);
                this.dirty = false;
                this.episodes = [];
                this.syncedKey = "";
                if (status === "authenticated") {
                    void this.loadRemote();
                } else if (status === "anonymous") {
                    void this.loadLocal();
                }
            },
            { fireImmediately: true }
        );

        // Order matters now, so reordering counts as a change too
        reaction(
            () => this.episodes.map((item) => item.episode.id).join(","),
            async (key) => {
                if (key === this.syncedKey) return;
                if (this.auth.isSignedIn) {
                    this.scheduleSave();
                } else if (this.auth.status === "anonymous") {
                    await this.syncLocal();
                }
            }
        );
    }

    private async loadLocal() {
        const generation = this.generation;
        const playlist: unknown = await localforage.getItem("hp_playlist");
        if (generation !== this.generation || !playlist || !Array.isArray(playlist)) return;

        runInAction(() => {
            this.episodes = playlist
                .filter((item) => item !== null)
                .map((item: PlaylistItem) => {
                    return {
                        played: item.played,
                        progress: item.progress,
                        episode: item.episode,
                        podcast: item.podcast,
                    };
                });
            this.syncedKey = this.episodes.map((item) => item.episode.id).join(",");
        });
    }

    /** Read the account's list again, e.g. after data was moved onto it */
    async reload() {
        if (this.auth.isSignedIn) await this.loadRemote();
    }

    private async loadRemote() {
        const generation = this.generation;
        try {
            const response = await this.client.get<PlaylistResponse>("/me/playlist");
            runInAction(() => {
                // Signed out, or in as someone else, while the request was on its way
                if (generation !== this.generation) return;
                this.episodes = response.episodes.map(({ podcast, ...episode }) => ({
                    played: false,
                    progress: 0,
                    episode: episode as Episode,
                    podcast: podcast as Podcast,
                }));
                this.syncedKey = this.episodes.map((item) => item.episode.id).join(",");
            });
        } catch (error) {
            // Not fatal: the list stays empty until the next visit, and nothing is overwritten
            console.warn("Could not load the playlist", error);
        }
    }

    private scheduleSave() {
        this.dirty = true;
        clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => void this.saveRemote(), SAVE_DELAY_MS);
    }

    private async saveRemote() {
        // One save at a time, so an older list never lands after a newer one
        if (this.saving) return;

        const generation = this.generation;
        while (this.dirty && generation === this.generation) {
            this.dirty = false;
            const ids = this.episodes.map((item) => item.episode.id);
            this.saving = true;
            try {
                await this.client.put("/me/playlist", { episode_ids: ids });
                this.syncedKey = ids.join(",");
            } catch (error) {
                console.warn("Could not save the playlist", error);
                // Left for the next change to send; the list is still complete in memory
                break;
            } finally {
                this.saving = false;
            }
        }
    }

    findEpisode(episode: Episode): number {
        return this.episodes.findIndex((item: PlaylistItem) => item.episode.id === episode.id);
    }

    inList(episode: Episode): boolean {
        return this.findEpisode(episode) !== -1;
    }

    async remove(episode: Episode) {
        const index = this.findEpisode(episode);
        if (index > -1) {
            this.episodes.splice(index, 1);
        }
    }

    async add(episode: Episode, podcast: Podcast) {
        this.episodes.push({
            played: false,
            episode,
            progress: 0,
            podcast,
        });
    }

    private async syncLocal() {
        await localforage.setItem(
            "hp_playlist",
            this.episodes.map((i: PlaylistItem) => {
                return {
                    played: i.played,
                    progress: i.progress,
                    episode: toJS(i.episode),
                    podcast: toJS(i.podcast),
                };
            })
        );
    }

    toggleOpen() {
        this.isOpen = !this.isOpen;
    }

    close() {
        this.isOpen = false;
    }

    toggleSort() {
        this.episodes = this.episodes.reverse();
    }

    /** The items whose episode or podcast title contains `text`, in playlist order; all of them for no text */
    matching(text: string): PlaylistItem[] {
        const needle = text.trim().toLowerCase();
        if (needle.length === 0) {
            return this.episodes;
        }
        return this.episodes.filter(
            (item: PlaylistItem) =>
                item.episode.title.toLowerCase().includes(needle) || item.podcast.name.toLowerCase().includes(needle)
        );
    }
}
